import { Icon, IContextualMenuItem, IIconStyles } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useSelector } from "react-redux";
import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { DocubirdCache, SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { DatasourceFieldHelper } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";

const rootContentType: string = "root";

const iconStyles: IIconStyles = {
    root: {
        display: "flex",
        marginLeft: 4,
        marginRight: 4
    }
}

const getDatasourceTemplates = (): NewDocumentTemplate[] => {
    const dsDocTemplates = CacheHelper.get(DocubirdCache.datasourceDocumentTemplates);
    if (Array.isArray(dsDocTemplates)) {
        return dsDocTemplates as NewDocumentTemplate[];
    }
    return [];
}

export function useMenuItem_NewDatasourceDocumentTemplate(
    selectedItems: any[],
    waitFor: boolean,
    newDocumentTemplates: NewDocumentTemplate[],
    openCreateTemplateModal: (selectedTemplate: NewDocumentTemplate) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue?.value);
    const enableNewTemplateFilters = useSelector((state: RootState) => state.tenantInfo.enableNewTemplateFilters);
    const newTemplateDocumentSetFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateDocumentSetFilters);
    const newTemplateFolderFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateFolderFilters);
    const newTemplateLibraryFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateLibraryFilters);
    const enforceStrictDocumentSetsOnly = useSelector((state: RootState) => state.tenantInfo.enforceStrictDocumentSetsOnly);
    const cacheUpdated = useSelector((state: RootState) => state.cacheUpdate.toggle);

    const [menuItem_NewDatasourceDocumentTemplate, setMenuItem_NewDatasourceDocumentTemplate] = React.useState<IContextualMenuItem>();
    const [datasourceDocumentTemplates, setDatasourceDocumentTemplates] = React.useState<NewDocumentTemplate[]>(getDatasourceTemplates());

    const newDatasourceDocumentTemplateItem: IContextualMenuItem = {
        key: "newDatasourceFileTemplate",
        disabled: !currentDirectory?.sharePointListId || waitFor === true || isBusySaving === true,
        text: languageStrings.Templates,
        iconProps: { iconName: "FileTemplate" },
        subMenuProps: {
            items: []
        }
    };

    React.useEffect(() => {
        setDatasourceDocumentTemplates(getDatasourceTemplates());
    }, [cacheUpdated]);

    React.useEffect(() => {
        if (selectedItems?.length === 0
            && datasourceDocumentTemplates.length > 0
            && currentSharepointFolder?.userPermissions?.canAddListItem
            && currentSharepointFolder?.userPermissions?.canEditListItem
            && !searchValue
            && !workingDirectories.some(x => x.type === ComponentTypes.SharePointList)) {

            var folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
            const canViewFolder = folderViewWrapper.canViewFolder();

            const isInFolder = ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));

            let currentContentype: string;
            if (enforceStrictDocumentSetsOnly || enableNewTemplateFilters) {
                if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                    currentContentype = SharePointFolderContentTypeIds.documentSet;
                } else if (isInFolder) {
                    currentContentype = SharePointFolderContentTypeIds.folder;
                } else {
                    currentContentype = rootContentType;
                }
            }

            if (enableNewTemplateFilters) {
                if ((currentContentype === rootContentType && !newTemplateLibraryFilters?.includes(newTemplateFilterEnum.file))
                    || (currentContentype === SharePointFolderContentTypeIds.folder && !newTemplateFolderFilters?.includes(newTemplateFilterEnum.file))
                    || (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.file))) {
                    setMenuItem_NewDatasourceDocumentTemplate(null);
                    return;
                }
            }

            if (enforceStrictDocumentSetsOnly
                && currentContentype === rootContentType
                && newDocumentTemplates?.some(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                setMenuItem_NewDatasourceDocumentTemplate(null);
                return;
            }

            if (canViewFolder || (!canViewFolder && !isInFolder)) {
                const currentSite: string[] = [currentDirectory?.siteDisplayName ?? ""];
                const currentLibrary: string[] = [currentDirectory?.DocumentLibrary ?? ""];

                datasourceDocumentTemplates
                    .filter(template => DatasourceFieldHelper.checkDatasourceAllowed(currentSite, currentLibrary, template.datasource))
                    .map(template => {
                        const subMenuItem: IContextualMenuItem = {
                            key: `new-${template.templateId}`,
                            text: template.title,
                            iconProps: {},
                            onRenderIcon: () => <Icon {...getFileTypeIconProps({ extension: template.extention, size: 20, imageFileType: "svg" })} styles={iconStyles} />,
                            onClick: () => {
                                openCreateTemplateModal(template);
                            }
                        }
                        newDatasourceDocumentTemplateItem.subMenuProps?.items?.push(subMenuItem);
                    });

                if (newDatasourceDocumentTemplateItem.subMenuProps?.items?.length > 0) {
                    setMenuItem_NewDatasourceDocumentTemplate(newDatasourceDocumentTemplateItem);
                    return;
                }
            }
        }

        setMenuItem_NewDatasourceDocumentTemplate(null);

    }, [selectedItems, waitFor, newDocumentTemplates, currentSharepointFolder, workingDirectories, currentDirectory, siteInfo, datasourceDocumentTemplates, searchValue, enableNewTemplateFilters, newTemplateLibraryFilters, newTemplateFolderFilters, newTemplateDocumentSetFilters]);

    return {
        menuItem_NewDatasourceDocumentTemplate
    }
}